/**
 * @file UpdateInvoiceFromOrderDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-09
 * @modified 2026-10-09
 *
 * "Update invoice from order…" in the order window (Cam, 9 Oct 2026). An
 * invoiced order whose words, item codes or supplier reference were corrected
 * after it was invoiced — and whose invoice was left as it was at the save —
 * is offered the changes again: GET
 * /api/orders/customer-orders/:id/invoice-edit-offer works out where the
 * order and its invoice now differ (services/invoiceEditOfferService
 * .currentOffer), shown in InvoiceEditOfferDialog and applied with the same
 * POST …/invoice-edit-offer/apply — guarded on what was shown, with the same
 * checks as the offer at the save (the lock, payments, posted-edit
 * permission). The UpdateInvoiceFromSuppliedDialog shape, for words.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import AlertDialog from './AlertDialog';
import InvoiceEditOfferDialog from './InvoiceEditOfferDialog';
import { hasInvoiceOffer } from '../utils/afterDelivery';

/**
 * @param {object} props
 * @param {number} props.orderId
 * @param {() => void} props.onClose
 * @param {(message: string, type?: string) => void} [props.onDone]
 */
function UpdateInvoiceFromOrderDialog({ orderId, onClose, onDone }) {
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    fetch(`${API_URL}/api/orders/customer-orders/${orderId}/invoice-edit-offer`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (!live) return;
        if (!d.success) { setError(d.error || 'The invoice changes could not be worked out.'); return; }
        setOffer(d.offer || { proposals: [], notices: [] });
      })
      .catch(err => live && setError(`The invoice changes could not be worked out: ${err.message}`));
    return () => { live = false; };
  }, [orderId]);

  if (error) return <AlertDialog isOpen type="error" message={error} onClose={onClose} />;
  if (!offer) return null;
  if (!hasInvoiceOffer(offer)) {
    return <AlertDialog isOpen type="info" message="The invoice already matches the order — its words, item codes and supplier reference agree." onClose={onClose} />;
  }
  return (
    <InvoiceEditOfferDialog
      offer={offer}
      orderId={orderId}
      onDone={(message, type) => {
        onClose();
        if (message && onDone) onDone(message, type);
      }}
    />
  );
}

export default UpdateInvoiceFromOrderDialog;
